import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, errText, downloadFile } from "@/lib/api";
import { GroupMultiFilter, matchGroups } from "@/components/GroupMultiFilter";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, scoreColor } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ChevronLeft, Settings2, X, FileDown, Upload } from "lucide-react";

function computeRow(components, scores, weights, backendComp, active) {
  const comp = {};
  components.forEach((c) => {
    if (c.type === "manual") {
      const graded = c.tasks.filter((t) => scores[t.id] !== undefined && scores[t.id] !== null && scores[t.id] !== "");
      const tw = graded.reduce((s, t) => s + (t.weight || 1), 0);
      const sum = graded.reduce((s, t) => s + (t.weight || 1) * Number(scores[t.id]), 0);
      comp[c.key] = { score: tw > 0 ? Math.round((sum / tw) * 100) / 100 : 0, graded: graded.length, total: c.tasks.length };
    } else {
      comp[c.key] = { score: (backendComp && backendComp[c.key] != null) ? backendComp[c.key] : 0, graded: 0, total: c.tasks.length };
    }
  });
  const act = (active && active.length) ? active : components.map((c) => c.key);
  const tw = act.reduce((s, k) => s + (weights[k] || 0), 0) || 1;
  const final = Math.round(act.reduce((s, k) => s + comp[k].score * (weights[k] || 0), 0) / tw * 100) / 100;
  const status = final >= 7 ? "Direkomendasikan" : "Tidak Direkomendasikan";
  return { comp, final, status };
}

const COMP_COLORS = { kehadiran: "text-emerald-600", partisipasi: "text-blue-600", kuis: "text-orange-600", individu: "text-amber-600", kelompok: "text-cyan-600" };

export default function Gradebook() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.is_super === true;
  const [gb, setGb] = useState(null);
  const [gbFilter, setGbFilter] = useState([]);
  const [scores, setScores] = useState({}); // {empId: {taskId: value}}
  const [showTasks, setShowTasks] = useState(false);
  const [newTask, setNewTask] = useState({ component: "individu", name: "", weight: 1 });
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef();

  const load = async () => {
    const { data } = await api.get("/gradebook");
    setGb(data);
    const map = {};
    data.employees.forEach((e) => { map[e.employee_id] = { ...e.scores }; });
    setScores(map);
  };
  useEffect(() => { load(); }, []);

  const weights = gb?.weights || {};
  const manualComponents = useMemo(() => (gb?.components || []).filter((c) => c.type === "manual"), [gb]);

  const saveTaskMeta = async (taskId, name, weight) => {
    setGb((prev) => ({
      ...prev,
      components: prev.components.map((c) => ({
        ...c,
        tasks: c.tasks.map((t) => (t.id === taskId ? { ...t, name, weight: Number(weight) || 1 } : t)),
      })),
    }));
    try {
      await api.put(`/tasks/${taskId}`, { name, weight: Number(weight) || 1 });
    } catch (e) { toast.error(errText(e)); }
  };

  const downloadTemplate = async () => {
    try {
      await downloadFile("/gradebook/template", "template_impor_nilai.xlsx");
      toast.success("Template diunduh");
    } catch (e) { toast.error(errText(e)); }
  };

  const importExcel = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gradebook/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.updated} nilai diimpor${data.group_updates ? `, ${data.group_updates} kelompok diperbarui` : ""}${data.errors.length ? `, ${data.errors.length} dilewati` : ""}`);
      if (data.errors.length) toast.error(data.errors[0]);
      await load();
    } catch (e) { toast.error(errText(e)); } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const saveScore = async (empId, taskId, raw) => {
    const value = raw === "" ? null : Math.max(0, Math.min(10, Number(raw)));
    setScores((prev) => ({ ...prev, [empId]: { ...prev[empId], [taskId]: value === null ? "" : value } }));
    try {
      await api.put("/task-scores", { employee_id: empId, task_id: taskId, score: value });
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/tasks", { component: newTask.component, name: newTask.name, weight: Number(newTask.weight) });
      toast.success("Tugas ditambahkan");
      setNewTask({ component: newTask.component, name: "", weight: 1 });
      await load();
    } catch (err) { toast.error(errText(err)); } finally { setAdding(false); }
  };

  const delTask = async (id) => {
    if (!window.confirm("Hapus tugas ini beserta semua nilainya?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Tugas dihapus");
    load();
  };

  if (!gb) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate("/penilaian")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"><ChevronLeft className="h-4 w-4" /> Rekap Penilaian</button>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Gradebook</h1>
          <p className="mt-1 text-slate-500">Matriks nilai Karyawan × Tugas. {canEdit ? "Isi sel untuk menilai; kolom Kuis & Kehadiran dihitung otomatis." : "Kolom Kuis & Kehadiran dihitung otomatis."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <GroupMultiFilter testid="gb-group-filter" names={gb.groups || []} value={gbFilter} onChange={setGbFilter} />
          <button data-testid="download-template-button" onClick={downloadTemplate} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition">
            <FileDown className="h-4 w-4" /> Template
          </button>
          {canEdit && (<>
          <input ref={importRef} data-testid="import-excel-input" type="file" accept=".xlsx" onChange={importExcel} className="hidden" />
          <button data-testid="import-excel-button" onClick={() => importRef.current?.click()} disabled={importing} className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition disabled:opacity-60">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Impor Excel
          </button>
          <button data-testid="manage-tasks-button" onClick={() => setShowTasks(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
            <Settings2 className="h-4 w-4" /> Kelola Tugas
          </button>
          </>)}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="bg-slate-50/70">
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-200 min-w-[200px]">Karyawan</th>
                {gb.components.map((c) => (
                  <th key={c.key} colSpan={Math.max(1, c.tasks.length) + 1} className="px-3 py-2 text-center text-xs font-bold uppercase border-b border-l border-slate-200">
                    <span className={COMP_COLORS[c.key]}>{c.label}</span>
                    <span className="block text-[10px] font-medium text-slate-400">bobot {Math.round(c.weight * 100)}%</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-xs font-semibold uppercase text-slate-400 border-b border-l border-slate-200">Nilai Akhir</th>
                <th className="px-3 py-2 text-center text-xs font-semibold uppercase text-slate-400 border-b border-slate-200">Status</th>
              </tr>
              <tr className="bg-white">
                <th className="sticky left-0 z-10 bg-white px-4 py-2 border-b border-slate-200"></th>
                {gb.components.map((c) => (
                  <Fragment key={c.key}>
                    {c.tasks.length === 0 && <th key={`${c.key}-empty`} className="px-2 py-2 text-[10px] text-slate-300 border-b border-l border-slate-200 italic">belum ada</th>}
                    {c.tasks.map((t) => (
                      <th key={t.id} className="px-2 py-2 text-[11px] font-medium text-slate-500 border-b border-l border-slate-200 min-w-[80px] max-w-[120px]">
                        {c.type === "manual" && canEdit ? (
                          <div className="flex flex-col gap-0.5">
                            <input
                              data-testid={`task-name-edit-${t.id}`}
                              defaultValue={t.name}
                              title="Ubah nama tugas"
                              onBlur={(ev) => { if (ev.target.value.trim() && ev.target.value !== t.name) saveTaskMeta(t.id, ev.target.value.trim(), t.weight); }}
                              className="w-full rounded border border-transparent hover:border-slate-200 focus:border-blue-400 bg-transparent px-1 py-0.5 text-[11px] font-semibold text-slate-600 outline-none text-center"
                            />
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-[9px] text-slate-400">bobot</span>
                              <input
                                data-testid={`task-weight-edit-${t.id}`}
                                type="number" min="0.1" step="0.1"
                                defaultValue={t.weight}
                                title="Ubah bobot tugas"
                                onBlur={(ev) => { const w = ev.target.value; if (w && Number(w) !== Number(t.weight)) saveTaskMeta(t.id, t.name, w); }}
                                className="w-10 rounded border border-slate-200 focus:border-blue-400 px-1 py-0.5 text-[10px] text-center outline-none"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="block truncate" title={t.name}>{t.name}</span>
                            {c.type === "manual" ? <span className="text-[9px] text-slate-400">bobot {t.weight}</span> : <span className="text-[9px] text-orange-400">auto</span>}
                          </>
                        )}
                      </th>
                    ))}
                    <th key={`${c.key}-sub`} className="px-2 py-2 text-[10px] font-semibold text-slate-400 border-b border-l border-slate-200">Rata²</th>
                  </Fragment>
                ))}
                <th className="px-3 py-2 border-b border-l border-slate-200"></th>
                <th className="px-3 py-2 border-b border-slate-200"></th>
              </tr>
            </thead>
            <tbody>
              {gb.employees.filter((emp) => matchGroups(gbFilter, emp.group)).map((emp) => {
                const es = scores[emp.employee_id] || {};
                const active = emp.active_components || [];
                const { comp, final, status } = computeRow(gb.components, es, weights, emp.component_scores, active);
                return (
                  <tr key={emp.employee_id} data-testid={`gb-row-${emp.employee_id}`} className="hover:bg-slate-50/60">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        {emp.photo_url ? <img src={fileUrl(emp.photo_url)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{emp.name[0]}</div>}
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate max-w-[130px]">{emp.name}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{emp.employee_id}{emp.group ? ` · ${emp.group}` : ""}</p>
                        </div>
                      </div>
                    </td>
                    {gb.components.map((c) => {
                      const isActive = active.includes(c.key);
                      if (!isActive) {
                        return (
                          <td key={c.key} colSpan={Math.max(1, c.tasks.length) + 1} className="border-b border-l border-slate-100 bg-slate-50/50 text-center align-middle" data-testid={`gb-inactive-${emp.employee_id}-${c.key}`}>
                            <span className="text-[10px] font-medium italic text-slate-300">tidak dinilai</span>
                          </td>
                        );
                      }
                      return (
                      <Fragment key={c.key}>
                        {c.tasks.length === 0 && <td key={`${c.key}-e`} className="border-b border-l border-slate-100" />}
                        {c.tasks.map((t) => (
                          <td key={t.id} className="px-1 py-1 border-b border-l border-slate-100 text-center">
                            {(c.type !== "manual" || !canEdit) ? (
                              <span className="text-xs font-semibold text-slate-600" data-testid={`gb-cell-${emp.employee_id}-${t.id}`}>{es[t.id] !== undefined && es[t.id] !== "" ? es[t.id] : <span className="text-slate-300">–</span>}</span>
                            ) : (
                              <input
                                data-testid={`gb-cell-${emp.employee_id}-${t.id}`}
                                type="number" min="0" max="10" step="0.5"
                                defaultValue={es[t.id] ?? ""}
                                onBlur={(ev) => { if (ev.target.value !== String(es[t.id] ?? "")) saveScore(emp.employee_id, t.id, ev.target.value); }}
                                className="w-12 rounded-md border border-slate-200 px-1 py-1 text-center text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                              />
                            )}
                          </td>
                        ))}
                        <td key={`${c.key}-s`} className={`px-2 py-1 border-b border-l border-slate-100 text-center text-xs font-bold ${COMP_COLORS[c.key]}`}>{comp[c.key].score}</td>
                      </Fragment>
                      );
                    })}
                    <td className={`px-3 py-1 border-b border-l border-slate-100 text-center font-heading font-extrabold ${scoreColor(final)}`} data-testid={`gb-final-${emp.employee_id}`}>{final}</td>
                    <td className="px-3 py-1 border-b border-slate-100 text-center"><StatusBadge status={status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showTasks && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowTasks(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold text-slate-900">Kelola Tugas</h3>
              <button onClick={() => setShowTasks(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>

            <form onSubmit={addTask} className="grid grid-cols-12 gap-2 items-end rounded-xl bg-slate-50 p-3 mb-4">
              <div className="col-span-5">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Komponen</label>
                <select data-testid="task-component-select" value={newTask.component} onChange={(e) => setNewTask({ ...newTask, component: e.target.value })} className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm">
                  {manualComponents.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-span-4">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Nama Tugas</label>
                <input data-testid="task-name-input" required value={newTask.name} onChange={(e) => setNewTask({ ...newTask, name: e.target.value })} placeholder="Tugas 3" className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Bobot</label>
                <input data-testid="task-weight-input" type="number" min="0.1" step="0.1" required value={newTask.weight} onChange={(e) => setNewTask({ ...newTask, weight: e.target.value })} className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" />
              </div>
              <button data-testid="add-task-button" type="submit" disabled={adding} className="col-span-1 flex items-center justify-center rounded-lg bg-blue-600 px-2 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </button>
            </form>

            <div className="space-y-4">
              {manualComponents.map((c) => (
                <div key={c.key}>
                  <p className={`text-xs font-bold uppercase ${COMP_COLORS[c.key]}`}>{c.label} <span className="text-slate-400 font-medium">({Math.round(c.weight * 100)}%)</span></p>
                  <div className="mt-1 space-y-1">
                    {c.tasks.length === 0 && <p className="text-xs text-slate-400 italic">Belum ada tugas</p>}
                    {c.tasks.map((t) => (
                      <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                        <span className="text-sm text-slate-700">{t.name} <span className="text-xs text-slate-400">· bobot {t.weight}</span></span>
                        <button data-testid={`del-task-${t.id}`} onClick={() => delTask(t.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400 pt-2 border-t">Komponen <span className="font-semibold text-orange-600">Kuis</span> otomatis memakai setiap kuis terpublikasi sebagai tugas; nilainya diambil dari hasil kuis karyawan.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
